import React, { useState, useRef, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Plus, Search, Edit, Trash2, Eye, Download, Users, UserCheck, ShieldCheck, Upload, FileSpreadsheet, Loader2, MessageCircle, IdCard, X, CheckCircle2, AlertTriangle } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import { usePagedEntity } from '../hooks/useEntity';
import { partisipanApi, partisipanExcelApi, dptApi, statsApi, desaApi } from '../lib/api';
import { toast } from '../hooks/use-toast';
import EntityFormDialog from '../components/EntityFormDialog';
import ConfirmDialog from '../components/ConfirmDialog';
import DetailModal from '../components/DetailModal';
import DptSearchBox from '../components/DptSearchBox';
import { Pager } from '../components/shared/UI';
import { KECAMATAN_LIST } from '../mock/mockData';
import { KECAMATAN_DESA } from '../constants/wilayahDesa';
import { KEANGGOTAAN_FIELDS } from '../lib/keanggotaanFields';
import { useAuth } from '../context/AuthContext';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../components/ui/table';

const PERAN_OPTIONS = [
  { value: 'simpatisan', label: 'Relawan' },
  { value: 'pelopor', label: 'Pelopor' },
  { value: 'non_pelopor', label: 'Non Pelopor' },
  { value: 'saksi', label: 'Saksi' },
];

const PERAN_LABEL_MAP = { simpatisan: 'Relawan', pelopor: 'Pelopor', non_pelopor: 'Non Pelopor', saksi: 'Saksi' };

const PERAN_BADGE = {
  simpatisan: 'bg-blue-100 text-blue-700 hover:bg-blue-100',
  pelopor: 'bg-amber-100 text-amber-700 hover:bg-amber-100',
  non_pelopor: 'bg-orange-100 text-orange-700 hover:bg-orange-100',
  saksi: 'bg-emerald-100 text-emerald-700 hover:bg-emerald-100',
};

const KADER_PERAN = ['pelopor', 'non_pelopor'];

// Opsi filter daftar (beda dari PERAN_OPTIONS di form): tambah "Semua Peran"
// dan satu opsi gabungan Pelopor+Non Pelopor, supaya klik "Kader Pelopor" di
// halaman Kecamatan/Desa/RW bisa langsung filter ke keduanya sekaligus.
const FILTER_PERAN_OPTIONS = [
  { value: 'all', label: 'Semua Peran' },
  ...PERAN_OPTIONS,
  { value: KADER_PERAN.join(','), label: 'Kader Pelopor (Pelopor + Non Pelopor)' },
];

const JABATAN_OPTS = ['Ketua DPC','Sekretaris DPC','Bendahara DPC','Kaderisasi','Humas','Litbang','Bidang Perempuan','Bidang Pemuda','Koordinator Kecamatan','Koordinator Desa','Koordinator RW','Kader Pelopor Aktif'].map(v => ({value: v, label: v}));

const normalizeWa = (hp) => { if (!hp) return ''; let s = String(hp).replace(/\D/g,''); if (s.startsWith('0')) s = '62' + s.slice(1); else if (!s.startsWith('62')) s = '62' + s; return s; };

// Daftar Desa/Kelurahan resmi per kecamatan (dari /api/wilayah/desa) - dipakai
// supaya field Desa jadi dropdown yang otomatis mengikuti Kecamatan yang
// dipilih, bukan ketik manual.
const useDesaMap = () => {
  const [desaMap, setDesaMap] = useState(KECAMATAN_DESA);
  useEffect(() => {
    desaApi.list().then(r => {
      if (r.data && Array.isArray(r.data)) {
        setDesaMap(Object.fromEntries(r.data.map(d => [d.kecamatan, d.desa])));
      }
    }).catch(() => {});
  }, []);
  return desaMap;
};

const resolveDesas = (kec, desaMap) => {
  if (!kec) return [];
  const map = (desaMap && Object.keys(desaMap).length > 0) ? desaMap : KECAMATAN_DESA;
  const direct = map[kec];
  if (direct) return direct;
  const entry = Object.entries(map).find(([k]) => k.toLowerCase() === String(kec).trim().toLowerCase());
  return entry ? entry[1] : (KECAMATAN_DESA[kec] || []);
};

const buildPartisipanFields = (desaMap) => [
  { type: 'section', label: 'Data Diri' },
  { name: 'nama', label: 'Nama Lengkap', required: true, placeholder: 'Nama sesuai KTP' },
  { name: 'peran', label: 'Peran', required: true, type: 'select', options: PERAN_OPTIONS },
  { name: 'hp', label: 'No. HP', placeholder: '0812xxxx' },
  { name: 'kecamatan', label: 'Kecamatan', required: true, type: 'select', options: KECAMATAN_LIST.map(k => ({ value: k.name, label: k.name })) },
  { name: 'desa', label: 'Desa / Kelurahan', type: 'select', placeholder: 'Pilih Kecamatan dulu',
    options: (data) => resolveDesas(data.kecamatan, desaMap).map(d => ({ value: d.nama, label: d.nama })) },
  { name: 'rw', label: 'RW', placeholder: 'RW 01' },
  { name: 'rt', label: 'RT', placeholder: 'RT 01' },
  { name: 'alamat', label: 'Alamat Lengkap', full: true, placeholder: 'Jl. ...' },

  { type: 'section', label: 'Penugasan Teritorial RW (Opsional)', description: 'Tandai jika anggota ini bertugas mengkoordinir pemenangan tingkat RW.' },
  { name: 'is_pj_rw', type: 'checkbox', label: 'Bertugas sebagai Penanggung Jawab RW (PJ RW)', hint: 'Otomatis tercatat di tab PJ RW dan rekapitulasi koordinator RW' },

  { type: 'section', label: 'Detail Pelopor / Non Pelopor', showIf: { peran: KADER_PERAN } },
  { name: 'jabatan', label: 'Jabatan Organisasi', required: true, type: 'select', options: JABATAN_OPTS, showIf: { peran: KADER_PERAN } },

  { type: 'section', label: 'Detail Saksi', showIf: { peran: 'saksi' } },
  { name: 'tps', label: 'No. TPS', required: true, placeholder: 'TPS 01', showIf: { peran: 'saksi' } },
  { name: 'status', label: 'Status Verifikasi', type: 'select', options: [{ value: 'pending', label: 'Pending' }, { value: 'terverifikasi', label: 'Terverifikasi' }], showIf: { peran: 'saksi' } },

  ...KEANGGOTAAN_FIELDS,
];

const Partisipan = () => {
  const { user } = useAuth();
  const canWrite = ['super_admin','admin_pusat','admin_input','koordinator','ketua_dpc'].includes(user?.role);
  const desaMap = useDesaMap();
  const FIELDS = buildPartisipanFields(desaMap);
  const [searchParams, setSearchParams] = useSearchParams();
  const pjRwAtauRelawan = searchParams.get('pj_rw_atau_relawan') === 'true';
  const [search, setSearch] = useState(searchParams.get('search') || searchParams.get('rw') || searchParams.get('q') || '');
  const [filterPeran, setFilterPeran] = useState(searchParams.get('peran') || 'all');
  const [filterKecamatan, setFilterKecamatan] = useState(searchParams.get('kecamatan') || '');
  const [filterDesa, setFilterDesa] = useState(searchParams.get('desa') || '');
  const { items, total, page, pages, limit, loading, setPage, create, update, remove, load } = usePagedEntity(partisipanApi, 'Partisipan', {
    search, filters: {
      peran: (filterPeran !== 'all' && !['rki', 'pengurus', 'tokoh'].includes(filterPeran)) ? filterPeran : undefined,
      kecamatan: filterKecamatan || undefined,
      desa: filterDesa || undefined,
      pj_rw_atau_relawan: pjRwAtauRelawan || undefined
    },
  });
  const [partisipanStats, setPartisipanStats] = useState(null);
  const isSuperAdmin = user?.role === 'super_admin';
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editData, setEditData] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);
  const [confirmDeleteAll, setConfirmDeleteAll] = useState(false);
  const [detail, setDetail] = useState(null);
  const [importing, setImporting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [dptPicked, setDptPicked] = useState(null);
  const fileRef = useRef();

  const ROLE_PILLS = [
    { id: 'all', label: 'Semua', count: partisipanStats?.total_orang ?? (partisipanStats ? ((partisipanStats?.simpatisan?.value || 0) + (partisipanStats?.kader?.value || 0) + (partisipanStats?.saksi?.value || 0)) : total) },
    { id: 'pelopor', label: 'Kader Pelopor', count: partisipanStats?.pelopor ?? partisipanStats?.pelopor_peran ?? (partisipanStats?.pelopor_stat?.value ?? 0) },
    { id: 'non_pelopor', label: 'Kader Non-Pelopor', count: partisipanStats?.non_pelopor_peran ?? (partisipanStats?.non_pelopor_stat?.value ?? 0) },
    { id: 'pj_rw', label: 'PJ RW', count: partisipanStats?.pj_rw ?? 0 },
    { id: 'saksi', label: 'Saksi TPS', count: partisipanStats?.saksi?.value ?? 0 },
    { id: 'simpatisan', label: 'Relawan', count: partisipanStats?.simpatisan?.value ?? 0 },
    { id: 'rki', label: 'Anggota RKI', link: '/rki' },
    { id: 'pengurus', label: 'Pengurus Organisasi', link: '/pengurus-dpc' },
    { id: 'tokoh', label: 'Data Tokoh', link: '/potensi-eksternal' },
  ];

  const loadStats = () => { statsApi.summary().then(r => setPartisipanStats(r.data)).catch(() => {}); };
  useEffect(() => { loadStats(); }, []);

  useEffect(() => {
    const openId = searchParams.get('open');
    if (!openId) return;
    partisipanApi.get(openId)
      .then(res => { setEditData(res.data); setDptPicked(null); setDialogOpen(true); })
      .catch(() => toast({ title: 'Data tidak ditemukan', description: 'Mungkin sudah dihapus.', variant: 'destructive' }))
      .finally(() => {
        searchParams.delete('open');
        setSearchParams(searchParams, { replace: true });
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const rwParam = searchParams.get('rw');
    const searchParam = searchParams.get('search');
    const kecParam = searchParams.get('kecamatan');
    const desaParam = searchParams.get('desa');
    const peranParam = searchParams.get('peran');

    if (rwParam || searchParam) {
      setSearch(searchParam || rwParam || '');
    }
    if (kecParam !== null && kecParam !== undefined) {
      setFilterKecamatan(kecParam || '');
    }
    if (desaParam !== null && desaParam !== undefined) {
      setFilterDesa(desaParam || '');
    }
    if (peranParam) {
      setFilterPeran(peranParam);
    }
  }, [searchParams]);

  // State pengecekan duplikasi Partisipan (HIJAU, ORANYE, NETRAL)
  const [duplicateInfo, setDuplicateInfo] = useState({
    status: 'NETRAL',
    candidates: [],
    existing: null,
    message: '',
    verifiedDistinct: false,
  });
  const duplicateDebounceRef = useRef(null);

  const handleCheckDuplicate = (dataToCheck) => {
    if (editData?.id) return;
    partisipanApi.checkDuplicate({
      nama: dataToCheck.nama,
      hp: dataToCheck.hp,
      kecamatan: dataToCheck.kecamatan,
      desa: dataToCheck.desa,
      dpt_id: dataToCheck.dpt_id,
    }).then(res => {
      setDuplicateInfo({
        status: res.data.status,
        candidates: res.data.candidates || [],
        existing: res.data.existing_profile || null,
        message: res.data.message || '',
        verifiedDistinct: false,
      });
    }).catch(() => {});
  };

  const handleFormChange = (formData) => {
    if (editData?.id) return;
    if (duplicateDebounceRef.current) clearTimeout(duplicateDebounceRef.current);
    duplicateDebounceRef.current = setTimeout(() => {
      if ((formData.nama?.trim().length >= 3 && formData.kecamatan) || formData.dpt_id || (formData.hp?.trim().length >= 8)) {
        handleCheckDuplicate(formData);
      }
    }, 400);
  };

  const handleSubmit = async (data) => {
    const payload = { ...data };
    if (payload.peran === 'pelopor' || payload.is_pelopor) {
      payload.is_pelopor = true;
      payload.peran = 'pelopor';
      if (!payload.peran_pelopor) {
        payload.peran_pelopor = payload.jabatan || 'Anggota Pelopor';
      }
    }
    const ok = editData?.id ? await update(editData.id, payload) : await create(payload);
    if (ok) loadStats();
    return ok;
  };

  const openAdd = () => {
    setEditData(null);
    setDptPicked(null);
    setDuplicateInfo({ status: 'NETRAL', candidates: [], existing: null, message: '', verifiedDistinct: false });
    setDialogOpen(true);
  };

  const openEdit = (p) => {
    setEditData(p);
    setDptPicked(null);
    setDuplicateInfo({ status: 'NETRAL', candidates: [], existing: null, message: '', verifiedDistinct: false });
    setDialogOpen(true);
  };

  const handleDptPick = (dpt) => {
    setDptPicked(dpt);

    // 1. Cocokkan Kecamatan ke format resmi KECAMATAN_LIST (mis. 'BANTARGADUNG' -> 'Bantargadung')
    const rawKec = (dpt.kecamatan || '').trim().replace(/^(kecamatan|kec\.?)\s+/i, '');
    const matchedKec = KECAMATAN_LIST.find(k => k.name.toLowerCase() === rawKec.toLowerCase() || k.name.toLowerCase() === (dpt.kecamatan || '').trim().toLowerCase())?.name || dpt.kecamatan;

    // 2. Cocokkan Desa/Kelurahan ke format resmi desaMap
    const desas = resolveDesas(matchedKec, desaMap);
    const rawDesa = (dpt.desa || '').trim().replace(/^(desa|kelurahan|kel\.?)\s+/i, '');
    const matchedDesa = desas.find(d => {
      const dName = typeof d === 'string' ? d : (d?.nama || '');
      const dClean = dName.replace(/^(desa|kelurahan|kel\.?)\s+/i, '').trim().toLowerCase();
      return dClean === rawDesa.toLowerCase() || dName.toLowerCase() === (dpt.desa || '').trim().toLowerCase();
    });
    const finalDesa = (typeof matchedDesa === 'string' ? matchedDesa : matchedDesa?.nama) || dpt.desa || '';

    // 3. Format nomor TPS yang rapi
    const rawTps = (dpt.tps || '').trim();
    const cleanTps = rawTps.replace(/^tps\s*/i, '');

    const pickedData = {
      nama: dpt.nama,
      kecamatan: matchedKec,
      desa: finalDesa,
      rw: dpt.rw,
      rt: dpt.rt,
      alamat: dpt.alamat,
      tps: cleanTps ? `TPS ${cleanTps}` : rawTps,
      dpt_id: dpt.id,
      peran: 'simpatisan',
    };
    setEditData(pickedData);
    handleCheckDuplicate(pickedData);
  };

  const handleDptClear = () => {
    setDptPicked(null);
    setEditData(null);
    setDuplicateInfo({ status: 'NETRAL', candidates: [], existing: null, message: '', verifiedDistinct: false });
  };

  const downloadTemplate = async () => {
    try {
      const res = await partisipanExcelApi.downloadTemplate();
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url; a.download = 'template_partisipan.xlsx';
      a.click(); window.URL.revokeObjectURL(url);
      toast({ title: 'Template terunduh' });
    } catch (e) {
      toast({ title: 'Gagal unduh template', variant: 'destructive' });
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await partisipanExcelApi.export();
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url; a.download = 'export_partisipan.xlsx';
      a.click(); window.URL.revokeObjectURL(url);
      toast({ title: 'Data berhasil diexport' });
    } catch (e) {
      toast({ title: 'Gagal export data', variant: 'destructive' });
    } finally { setExporting(false); }
  };

  const handleImport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImporting(true);
    try {
      const res = await partisipanExcelApi.import(file);
      const { inserted, errors, total_errors } = res.data;
      toast({
        title: `Import selesai — ${inserted} data ditambahkan`,
        description: total_errors > 0 ? `${total_errors} baris gagal (cek konsol)` : 'Semua baris berhasil'
      });
      if (errors?.length) console.warn('Import errors:', errors);
      await load();
      loadStats();
    } catch (err) {
      toast({ title: 'Gagal import', description: err.response?.data?.detail || err.message, variant: 'destructive' });
    } finally { setImporting(false); e.target.value = ''; }
  };

  const handleDeleteAll = async () => {
    try {
      const res = await partisipanApi.deleteAll();
      toast({ title: `${res.data.deleted} data Partisipan dihapus` });
      await load();
      loadStats();
    } catch (e) {
      toast({ title: 'Gagal menghapus', description: e.response?.data?.detail || e.message, variant: 'destructive' });
    }
  };

  const totalPartisipan = partisipanStats?.total_orang ?? ((partisipanStats?.simpatisan?.value || 0) + (partisipanStats?.kader?.value || 0) + (partisipanStats?.saksi?.value || 0));
  const stats = [
    { l: 'Total Partisipan', v: partisipanStats ? totalPartisipan : total, c: 'text-orange-600', bg: 'bg-orange-50', icon: Users },
    { l: 'Relawan', v: partisipanStats?.simpatisan?.value ?? 0, c: 'text-blue-600', bg: 'bg-blue-50', icon: Users },
    { l: 'Kader Pelopor', v: partisipanStats?.pelopor ?? partisipanStats?.pelopor_peran ?? (partisipanStats?.pelopor_stat?.value ?? 0), c: 'text-amber-600', bg: 'bg-amber-50', icon: UserCheck },
    { l: 'Saksi', v: partisipanStats?.saksi?.value ?? 0, c: 'text-emerald-600', bg: 'bg-emerald-50', icon: ShieldCheck },
  ];

  const duplicateBanner = (() => {
    if (duplicateInfo.status === 'HIJAU' && duplicateInfo.existing) {
      const ex = duplicateInfo.existing;
      return (
        <div className="mb-4 p-4 rounded-xl bg-emerald-50 border border-emerald-300 text-emerald-950">
          <div className="flex items-start gap-3">
            <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="font-extrabold text-sm text-emerald-900">Profil Sudah Terdaftar di BP3 (Terkonfirmasi)</p>
              <p className="text-xs text-emerald-800 mt-1">
                Warga DPT ini sudah tercatat sebagai <b>{ex.nama}</b> (Peran: {PERAN_LABEL_MAP[ex.peran] || ex.peran}{ex.jabatan ? ` · ${ex.jabatan}` : ''}) di Kecamatan {ex.kecamatan}{ex.desa ? `, Desa ${ex.desa}` : ''}.
                Pembuatan profil baru diblokir untuk mencegah duplikasi identitas.
              </p>
              <div className="mt-3">
                <Button
                  type="button"
                  size="sm"
                  onClick={() => openEdit(ex)}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs gap-1.5"
                >
                  <UserCheck className="w-4 h-4" /> Buka Profil {ex.nama} untuk Kelola Jabatan / Penugasan
                </Button>
              </div>
            </div>
          </div>
        </div>
      );
    }

    if (duplicateInfo.status === 'ORANYE' && duplicateInfo.candidates?.length > 0) {
      return (
        <div className="mb-4 p-4 rounded-xl bg-amber-50 border border-amber-300 text-amber-950">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="font-extrabold text-sm text-amber-900">Perhatian: Ditemukan {duplicateInfo.candidates.length} Kandidat Serupa</p>
              <p className="text-xs text-amber-800 mt-1">
                Sistem mendeteksi kemiripan nama, wilayah, atau nomor HP dengan data partisipan berikut.
                Periksa apakah ini orang yang sama atau warga berbeda:
              </p>
              <div className="mt-2.5 space-y-2">
                {duplicateInfo.candidates.map((c) => (
                  <div key={c.id} className="p-2.5 rounded-lg bg-white/90 border border-amber-200 flex items-center justify-between gap-3 text-xs">
                    <div>
                      <p className="font-bold text-gray-900">{c.nama} <span className="text-gray-500 font-normal">({PERAN_LABEL_MAP[c.peran] || c.peran}{c.jabatan ? ` · ${c.jabatan}` : ''})</span></p>
                      <p className="text-gray-600 mt-0.5">Kec. {c.kecamatan}{c.desa && `, Desa ${c.desa}`}{c.tps && ` · TPS ${c.tps}`} · HP: {c.hp_masked || '-'}</p>
                      <p className="text-amber-700 font-medium text-[11px] mt-0.5">Petunjuk: {c.match_type}</p>
                    </div>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => openEdit(c)}
                      className="text-xs font-semibold h-8 border-amber-300 text-amber-900 hover:bg-amber-100 flex-shrink-0"
                    >
                      Orang yang Sama (Buka Profil)
                    </Button>
                  </div>
                ))}
              </div>
              <div className="mt-3 pt-3 border-t border-amber-200/80 flex items-center gap-2">
                <input
                  type="checkbox"
                  id="confirm_distinct_person"
                  checked={duplicateInfo.verifiedDistinct}
                  onChange={(e) => setDuplicateInfo(prev => ({ ...prev, verifiedDistinct: e.target.checked }))}
                  className="rounded border-amber-400 text-orange-600 focus:ring-orange-500 h-4 w-4"
                />
                <label htmlFor="confirm_distinct_person" className="text-xs font-bold text-amber-950 cursor-pointer">
                  Saya telah memverifikasi bahwa ini adalah warga yang BERBEDA (Lanjutkan Pendaftaran)
                </label>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return null;
  })();

  return (
    <div className="space-y-4 sm:space-y-5">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
        {stats.map(s => (
          <div key={s.l} className="bg-white rounded-xl p-3.5 sm:p-4 card-shadow card-hover">
            <div className={`w-9 h-9 rounded-lg ${s.bg} flex items-center justify-center mb-2.5`}>
              <s.icon className={`w-4.5 h-4.5 ${s.c}`} />
            </div>
            <p className="text-xs font-semibold text-gray-500">{s.l}</p>
            <h3 className="text-xl sm:text-2xl font-black text-gray-900 mt-0.5">{new Intl.NumberFormat('id-ID').format(s.v)}</h3>
          </div>
        ))}
      </div>

      <div className="bg-white rounded-xl p-4 sm:p-5 card-shadow">
        {/* TAB FILTER PERAN HORIZONTAL SESUAI DESAIN FINAL */}
        <div className="flex items-center gap-1.5 overflow-x-auto pb-2.5 border-b border-gray-100 mb-4">
          {ROLE_PILLS.map((pill) => {
            const isActive = filterPeran === pill.id;
            return pill.link ? (
              <a
                key={pill.id}
                href={pill.link}
                className="px-3 py-1 rounded-lg text-xs font-semibold transition-all flex items-center gap-1.5 flex-shrink-0 bg-gray-50 text-gray-600 hover:bg-orange-50 hover:text-orange-600 border border-gray-200"
              >
                <span>{pill.label}</span>
                <span className="text-[10px] text-gray-400">↗</span>
              </a>
            ) : (
              <button
                key={pill.id}
                type="button"
                onClick={() => setFilterPeran(pill.id)}
                className={`px-3 py-1 rounded-lg text-xs font-semibold transition-all flex items-center gap-1.5 flex-shrink-0 ${
                  isActive
                    ? 'bg-orange-600 text-white shadow-sm font-bold'
                    : 'bg-white text-gray-700 hover:bg-orange-50 border border-gray-200'
                }`}
              >
                <span>{pill.label}</span>
                {pill.count !== undefined && (
                  <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-bold ${isActive ? 'bg-white/20 text-white' : 'bg-gray-100 text-gray-600'}`}>
                    {new Intl.NumberFormat('id-ID').format(pill.count)}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* TOOLBAR SEARCH & ACTIONS */}
        <div className="flex items-center justify-between mb-4 gap-2.5 flex-wrap">
          <div className="flex items-center gap-2 flex-1 min-w-[280px] flex-wrap">
            {/* Search Input */}
            <div className="relative flex-1 min-w-[200px] max-w-sm">
              <Search className="w-3.5 h-3.5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <Input
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Cari nama, No. HP, atau wilayah..."
                className="pl-8.5 h-9 text-xs"
              />
            </div>

            {/* Dropdown Filter Kecamatan */}
            <Select
              value={filterKecamatan || 'all'}
              onValueChange={v => { setFilterKecamatan(v === 'all' ? '' : v); setFilterDesa(''); }}
            >
              <SelectTrigger className="w-40 sm:w-44 h-9 text-xs font-medium">
                <SelectValue placeholder="Semua Kecamatan" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Semua Kecamatan</SelectItem>
                {KECAMATAN_LIST.map(k => (
                  <SelectItem key={k.name} value={k.name}>{k.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Dropdown Filter Desa (Bila Kecamatan Dipilih) */}
            {filterKecamatan && (
              <Select
                value={filterDesa || 'all'}
                onValueChange={v => setFilterDesa(v === 'all' ? '' : v)}
              >
                <SelectTrigger className="w-40 sm:w-44 h-9 text-xs font-medium bg-orange-50/50 border-orange-200">
                  <SelectValue placeholder="Semua Desa" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Semua Desa di {filterKecamatan}</SelectItem>
                  {(desaMap[filterKecamatan] || []).map(d => (
                    <SelectItem key={d.nama} value={d.nama}>{d.nama}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}

            {pjRwAtauRelawan && (
              <span className="flex items-center gap-1.5 h-8 px-2.5 rounded-lg bg-orange-100 text-orange-800 text-xs font-semibold">
                PJ RW & Relawan
                <button
                  type="button"
                  onClick={() => {
                    const next = new URLSearchParams(searchParams);
                    next.delete('pj_rw_atau_relawan');
                    setSearchParams(next, { replace: true });
                  }}
                  className="hover:text-red-600 font-bold ml-0.5"
                  title="Hapus filter PJ RW & Relawan"
                >
                  ✕
                </button>
              </span>
            )}

            {(filterKecamatan || filterDesa) && (
              <button
                onClick={() => { setFilterKecamatan(''); setFilterDesa(''); }}
                className="flex items-center gap-1 h-8 px-2.5 rounded-lg bg-orange-100 text-orange-700 text-xs font-bold hover:bg-orange-200"
              >
                Reset Filter ✕
              </button>
            )}
          </div>

          <div className="flex gap-2 flex-wrap items-center">
            <Button variant="outline" onClick={handleExport} disabled={exporting} className="h-9 text-xs gap-1.5 font-semibold">
              {exporting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />} Export
            </Button>
            {canWrite && (
              <>
                <input ref={fileRef} type="file" accept=".xlsx,.xls" onChange={handleImport} className="hidden" />
                <Button variant="outline" onClick={downloadTemplate} className="h-9 text-xs gap-1.5 font-semibold">
                  <FileSpreadsheet className="w-3.5 h-3.5" /> Template
                </Button>
                <Button variant="outline" onClick={() => fileRef.current?.click()} disabled={importing} className="h-9 text-xs gap-1.5 font-semibold">
                  {importing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />} Import
                </Button>
                <Button onClick={openAdd} className="h-9 text-xs bg-orange-500 hover:bg-orange-600 gap-1.5 font-bold">
                  <Plus className="w-3.5 h-3.5" /> Tambah Orang
                </Button>
              </>
            )}
            {isSuperAdmin && total > 0 && (
              <Button variant="outline" onClick={() => setConfirmDeleteAll(true)} className="h-9 text-xs gap-1.5 font-semibold border-red-300 text-red-700 hover:bg-red-50">
                <Trash2 className="w-3.5 h-3.5" /> Hapus Semua
              </Button>
            )}
          </div>
        </div>

        {/* TABEL DATA ORANG (ZERO-NIK) */}
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-orange-50/50">
                <TableHead className="font-extrabold text-xs">Nama</TableHead>
                <TableHead className="font-extrabold text-xs">Kategori / Peran</TableHead>
                <TableHead className="font-extrabold text-xs">Wilayah</TableHead>
                <TableHead className="font-extrabold text-xs">Kontak (WhatsApp)</TableHead>
                <TableHead className="font-extrabold text-xs">Status</TableHead>
                <TableHead className="font-extrabold text-xs text-center w-28">Aksi</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={6} className="text-center py-8 text-gray-400 font-semibold text-xs">Memuat data orang...</TableCell></TableRow>
              ) : items.length === 0 ? (
                <TableRow><TableCell colSpan={6} className="text-center py-8 text-gray-400 font-semibold text-xs">Belum ada data orang. Klik &quot;Tambah Orang&quot; untuk mulai.</TableCell></TableRow>
              ) : items.map(p => (
                <TableRow key={p.id} className="hover:bg-orange-50/30 text-xs">
                  <TableCell>
                    <button onClick={() => setDetail(p)} className="font-bold text-gray-900 hover:text-orange-600 text-left block">
                      {p.nama}
                    </button>
                    {p.jabatan && <span className="text-[10px] text-gray-500 font-medium">{p.jabatan}</span>}
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap items-center gap-1.5">
                      <Badge className={PERAN_BADGE[p.peran] || 'bg-gray-100 text-gray-700'}>
                        {PERAN_LABEL_MAP[p.peran] || p.peran}
                      </Badge>
                      {(p.is_pj_rw || p.jabatan === 'Koordinator RW') && (
                        <Badge className="bg-purple-100 text-purple-700 hover:bg-purple-100 font-bold border border-purple-200">
                          PJ {p.rw || 'RW'}
                        </Badge>
                      )}
                      {p.is_rki && (
                        <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100 font-semibold border border-emerald-200">
                          RKI
                        </Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="font-medium text-gray-600">
                    <div className="flex items-center gap-1">
                      <span className="font-bold text-gray-900">{p.kecamatan}</span>
                      {p.desa && <span>· {p.desa}</span>}
                      {p.rw && <span className="text-gray-400">· {p.rw}</span>}
                    </div>
                  </TableCell>
                  <TableCell>
                    {p.hp ? (
                      <a
                        href={`https://wa.me/${normalizeWa(p.hp)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 px-2 py-1 rounded-lg bg-emerald-50 text-emerald-700 hover:bg-emerald-100 font-bold text-[11px]"
                      >
                        <MessageCircle className="w-3.5 h-3.5" />
                        <span>{p.hp}</span>
                      </a>
                    ) : (
                      <span className="text-gray-400 text-[11px]">-</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <span className="text-[11px] font-semibold text-gray-600">
                      {p.peran === 'saksi' ? `TPS ${p.tps || '-'} (${p.status || 'Aktif'})` : (p.status || 'Aktif')}
                    </span>
                  </TableCell>
                  <TableCell className="text-center">
                    <div className="flex items-center justify-center gap-1">
                      <button onClick={() => setDetail(p)} className="p-1.5 rounded-lg hover:bg-orange-50 text-gray-500 hover:text-orange-600" title="Lihat detail">
                        <Eye className="w-3.5 h-3.5" />
                      </button>
                      {canWrite && (
                        <button onClick={() => openEdit(p)} className="p-1.5 rounded-lg hover:bg-orange-50 text-gray-500 hover:text-orange-600" title="Edit">
                          <Edit className="w-3.5 h-3.5" />
                        </button>
                      )}
                      {canWrite && (
                        <button onClick={() => setConfirmDel(p)} className="p-1.5 rounded-lg hover:bg-red-50 text-gray-500 hover:text-red-600" title="Hapus">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <Pager page={page} pages={pages} total={total} limit={limit} onPageChange={setPage} />
        </div>
      </div>

      <EntityFormDialog
        open={dialogOpen} onOpenChange={(o) => {
          setDialogOpen(o);
          if (!o) {
            setDptPicked(null);
            setDuplicateInfo({ status: 'NETRAL', candidates: [], existing: null, message: '', verifiedDistinct: false });
          }
        }}
        title={editData?.id ? `Edit ${editData.nama}` : 'Tambah Partisipan Baru'}
        description="Pilih peran (Relawan/Pelopor/Non Pelopor/Saksi) — form akan menyesuaikan field yang dibutuhkan."
        extraHeader={!editData?.id && (
          <DptSearchBox
            picked={dptPicked}
            onPick={handleDptPick}
            onClear={handleDptClear}
            defaultKecamatan={filterKecamatan}
          />
        )}
        alertBanner={!editData?.id ? duplicateBanner : null}
        submitDisabled={!editData?.id && (duplicateInfo.status === 'HIJAU' || (duplicateInfo.status === 'ORANYE' && !duplicateInfo.verifiedDistinct))}
        onDataChange={!editData?.id ? handleFormChange : undefined}
        fields={FIELDS} initialData={editData || { peran: 'simpatisan' }} onSubmit={handleSubmit}
      />
      <ConfirmDialog open={!!confirmDel} onOpenChange={(o) => !o && setConfirmDel(null)}
        title="Hapus Partisipan?" description={`Data "${confirmDel?.nama}" akan dihapus permanen.`}
        onConfirm={async () => { await remove(confirmDel.id); loadStats(); }}
      />
      <ConfirmDialog open={confirmDeleteAll} onOpenChange={setConfirmDeleteAll}
        title={`Hapus SEMUA ${new Intl.NumberFormat('id-ID').format(total)} Data Partisipan?`}
        description="Seluruh data Relawan, Kader Pelopor, dan Saksi akan dihapus permanen dan tidak bisa dikembalikan. Target/baseline per kecamatan dan data DPT tidak ikut terhapus."
        onConfirm={handleDeleteAll}
      />
      <DetailModal open={!!detail} onOpenChange={(o) => !o && setDetail(null)} data={detail} entityLabel={detail ? (PERAN_OPTIONS.find(p => p.value === detail.peran)?.label || 'Partisipan') : ''} />
    </div>
  );
};

export default Partisipan;
